import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Trophy, Crown, Medal, Flame, Swords, ArrowRight, Users } from "lucide-react";
import { EpicPage, EpicHero, EpicCard, Chip } from "@/components/Epic";
import { Button } from "@/components/ui/button";
import { WeekWinnerBanner } from "@/components/WeekWinnerBanner";

const PODIUM = {
  1: { ring: "ring-amber-400", bg: "from-amber-100 to-white", badge: "bg-amber-400 text-black", h: "h-28", label: "Champion" },
  2: { ring: "ring-slate-300", bg: "from-slate-100 to-white", badge: "bg-slate-300 text-slate-900", h: "h-20", label: "Runner-up" },
  3: { ring: "ring-orange-300", bg: "from-orange-100 to-white", badge: "bg-orange-300 text-orange-950", h: "h-16", label: "Third" },
};

const Avatar = ({ u, size = "w-10 h-10", ring = "" }) => (
  <div className={`${size} rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-black font-bold shrink-0 ring-2 ring-white shadow ${ring}`}>{u.name[0]?.toUpperCase()}</div>
);

function Podium({ top }) {
  const order = [top[1], top[0], top[2]].filter(Boolean);
  return (
    <EpicCard className="p-6 sm:p-8 overflow-hidden relative" testId="leaderboard-podium">
      <div className="absolute inset-x-0 top-0 h-40 bg-[radial-gradient(ellipse_at_top,rgba(245,158,11,0.14),transparent_65%)] pointer-events-none" />
      <div className="relative grid grid-cols-3 gap-3 items-end max-w-2xl mx-auto">
        {order.map((u, i) => {
          const p = PODIUM[u.rank];
          return (
            <motion.div key={u.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.1 }} className="flex flex-col items-center text-center" data-testid={`podium-${u.rank}`}>
              {u.rank === 1 && <Crown className="w-6 h-6 text-amber-500 mb-1" />}
              <Avatar u={u} size={u.rank === 1 ? "w-16 h-16 text-xl" : "w-12 h-12"} ring={`ring-4 ${p.ring}`} />
              <div className="mt-2 font-bold text-slate-900 text-sm truncate max-w-[9rem]">{u.name}{u.is_me && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500 text-black font-bold">YOU</span>}</div>
              <div className="text-xs font-mono font-bold text-emerald-700">{u.xp.toLocaleString()} XP</div>
              <div className={`mt-2 w-full ${p.h} rounded-t-2xl bg-gradient-to-b ${p.bg} border border-b-0 border-slate-200/80 flex items-start justify-center pt-2`}>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${p.badge}`}>{p.label}</span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </EpicCard>
  );
}

export default function Leaderboard() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(() => localStorage.getItem("pc_lb_period") || "all");
  const [board, setBoard] = useState(null);
  useEffect(() => {
    let alive = true;
    setBoard(null);
    localStorage.setItem("pc_lb_period", period);
    api.get(`/leaderboard?period=${period}`).then((r) => alive && setBoard(r.data)).catch((err) => console.warn("[Leaderboard] request failed", err));
    return () => { alive = false; };
  }, [period]);
  const weekly = period === "week";
  const daysLeft = 7 - ((new Date().getUTCDay() + 6) % 7);
  const tabs = (
    <div className="inline-flex rounded-full bg-slate-100 border border-slate-200 p-1" role="tablist" data-testid="leaderboard-tabs">
      {[["all", "All time"], ["week", "This week"]].map(([k, label]) => (
        <button key={k} role="tab" aria-selected={period === k} onClick={() => setPeriod(k)}
          className={`px-4 h-8 rounded-full text-sm font-semibold transition-colors ${period === k ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:text-slate-900"}`} data-testid={`leaderboard-tab-${k}`}>
          {label}
        </button>
      ))}
    </div>
  );
  if (!board) return <EpicPage testId="leaderboard-page"><div className="flex items-center justify-center py-32 text-emerald-600"><Loader2 className="w-6 h-6 animate-spin" /></div></EpicPage>;

  const me = board.find((u) => u.is_me);
  const above = me && me.rank > 1 ? board.find((u) => u.rank === me.rank - 1) : null;
  const gap = above ? above.xp - me.xp : 0;

  return (
    <EpicPage testId="leaderboard-page">
      <EpicHero overline="Community" title="World" accent="Leaderboard"
        subtitle={weekly ? "Fresh start every Monday. This week's XP only — so a new rep can top the table." : "Compete with sellers everywhere. Every rep earns XP — every XP moves you up."} glow="bg-amber-300/20"
        aside={me ? (
          <div className="rounded-2xl bg-white/80 backdrop-blur border border-slate-200/80 shadow-sm p-4 sm:min-w-[280px]" data-testid="my-rank-card">
            <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700">Your position{weekly ? " this week" : ""}</div>
            <div className="mt-1 flex items-end gap-2"><span className="text-4xl font-extrabold font-display tracking-tight text-slate-900">#{me.rank}</span><span className="text-sm text-slate-500 mb-1.5">of {board.length}</span></div>
            <div className="text-xs text-slate-600 mt-1">{above ? <>Only <span className="font-bold text-slate-900">{gap.toLocaleString()} XP</span> behind {above.name}</> : "You're leading the pack."}</div>
            <Button size="sm" onClick={() => navigate("/app/roleplay")} className="mt-3 h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold w-full" data-testid="leaderboard-cta"><Swords className="w-4 h-4 mr-1.5" /> Earn XP now</Button>
          </div>
        ) : weekly ? (
          <div className="rounded-2xl bg-white/80 backdrop-blur border border-emerald-200 shadow-sm p-4 sm:min-w-[280px]" data-testid="weekly-join-card">
            <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700">Not on this week's board yet</div>
            <div className="text-sm text-slate-700 mt-1">One roleplay puts you on it. Top spot is only <span className="font-bold">{(board[0]?.xp || 0).toLocaleString()} XP</span> away.</div>
            <Button size="sm" onClick={() => navigate("/app/roleplay")} className="mt-3 h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold w-full" data-testid="leaderboard-cta"><Swords className="w-4 h-4 mr-1.5" /> Start a roleplay</Button>
          </div>
        ) : null}>
        {tabs}
        <Chip dark><Users className="w-3.5 h-3.5" /> {board.length} sellers</Chip>
        {weekly
          ? <Chip><Flame className="w-3.5 h-3.5 text-amber-500" /> Resets in {daysLeft} day{daysLeft === 1 ? "" : "s"}</Chip>
          : <Chip><Flame className="w-3.5 h-3.5 text-amber-500" /> Ranked by total XP</Chip>}
      </EpicHero>

      {weekly && <WeekWinnerBanner />}

      {board.length >= 3 && <Podium top={board.slice(0, 3)} />}

      <EpicCard className="overflow-hidden" testId="leaderboard-table">
        {board.length === 0 && <div className="p-8 text-center text-slate-500" data-testid="leaderboard-empty">{weekly ? "Nobody has scored yet this week — one roleplay takes the top spot." : "No competitors yet. Be the first on the board."}</div>}
        {board.map((u, i) => (
          <motion.div key={u.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.03 }}
            className={`flex items-center gap-4 px-5 py-3.5 border-b border-slate-100 last:border-0 transition-colors hover:bg-slate-50 ${u.is_me ? "bg-emerald-50/70" : ""}`} data-testid={`leaderboard-row-${u.rank}`}>
            <div className="w-8 flex justify-center">
              {u.rank <= 3 ? <Medal className={`w-5 h-5 ${u.rank === 1 ? "text-amber-500" : u.rank === 2 ? "text-slate-400" : "text-orange-400"}`} /> : <span className="text-sm font-mono font-bold text-slate-500">{u.rank}</span>}
            </div>
            <Avatar u={u} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 truncate flex items-center gap-2">{u.name}{u.is_me && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500 text-black font-bold">YOU</span>}</div>
              <div className="text-xs text-slate-500">Level {u.level} · {u.conversations} calls{u.win_rate != null ? ` · ${u.win_rate}% win` : weekly ? " this week" : ""}</div>
            </div>
            <div className="hidden sm:block w-28">
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.max(4, (u.xp / Math.max(1, board[0].xp)) * 100)}%` }} /></div>
            </div>
            <div className="text-right">
              <div className="font-extrabold text-slate-900 font-display tracking-tight">{u.xp.toLocaleString()}</div>
              <div className="text-[10px] text-slate-500 uppercase font-mono">XP</div>
            </div>
          </motion.div>
        ))}
      </EpicCard>

      <div className="grid sm:grid-cols-3 gap-4">
        {[{ icon: Trophy, t: "Battles", d: "Challenge a rep head-to-head on the same customer.", to: "/app/battles" },
          { icon: Medal, t: "Tournaments", d: "Weekly knockouts — win the bracket, take the crown.", to: "/app/tournaments" },
          { icon: Crown, t: "Teams", d: "Join your team and climb the team table together.", to: "/app/teams" }].map((x, i) => (
          <EpicCard key={i} className="p-5 group cursor-pointer hover:border-emerald-300 hover:shadow-md transition-all" onClick={() => navigate(x.to)} testId={`community-feature-${i}`}>
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><x.icon className="w-5 h-5" /></div>
            <div className="font-bold text-slate-900 mt-3 flex items-center gap-1.5 group-hover:text-emerald-700">{x.t} <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" /></div>
            <div className="text-sm text-slate-600 mt-1">{x.d}</div>
          </EpicCard>
        ))}
      </div>
    </EpicPage>
  );
}
